import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import VideoCard from '@/components/base/VideoCard';
import Pagination from '@/components/base/Pagination';
import { useSeo } from '@/hooks/useSeo';
import { categories, videos } from '@/mocks/videos';

const PAGE_SIZE = 8;

type SortKey = 'latest' | 'views' | 'rating';

const SORTS: { key: SortKey; label: string }[] = [
  { key: 'latest', label: '更新时间' },
  { key: 'views', label: '播放量' },
  { key: 'rating', label: '评分' },
];

export default function CategoryPage() {
  const { categoryId = '' } = useParams();
  const category = categories.find((c) => c.id === categoryId);

  const [tag, setTag] = useState('全部');
  const [region, setRegion] = useState('全部');
  const [year, setYear] = useState('全部');
  const [sort, setSort] = useState<SortKey>('latest');
  const [page, setPage] = useState(1);

  useEffect(() => {
    setTag('全部');
    setRegion('全部');
    setYear('全部');
    setSort('latest');
    setPage(1);
  }, [categoryId]);

  useEffect(() => {
    setPage(1);
  }, [tag, region, year, sort]);

  const categoryVideos = useMemo(
    () => videos.filter((v) => v.categoryId === categoryId),
    [categoryId],
  );

  const tags = useMemo(() => {
    const set = new Set<string>();
    categoryVideos.forEach((v) => v.tags.forEach((t) => set.add(t)));
    return ['全部', ...Array.from(set)];
  }, [categoryVideos]);

  const regions = useMemo(() => {
    const set = new Set<string>();
    categoryVideos.forEach((v) => set.add(v.region));
    return ['全部', ...Array.from(set)];
  }, [categoryVideos]);

  const years = useMemo(() => {
    const set = new Set<string>();
    categoryVideos.forEach((v) => set.add(v.year));
    return ['全部', ...Array.from(set).sort((a, b) => b.localeCompare(a))];
  }, [categoryVideos]);

  const filtered = useMemo(() => {
    const list = categoryVideos.filter((v) => {
      const okTag = tag === '全部' || v.tags.includes(tag);
      const okRegion = region === '全部' || v.region === region;
      const okYear = year === '全部' || v.year === year;
      return okTag && okRegion && okYear;
    });
    const sorted = [...list];
    if (sort === 'views') sorted.sort((a, b) => b.views - a.views);
    else if (sort === 'rating') sorted.sort((a, b) => b.rating - a.rating);
    else sorted.sort((a, b) => b.uploadDate.localeCompare(a.uploadDate));
    return sorted;
  }, [categoryVideos, tag, region, year, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useSeo({
    title: category ? `${category.name} - 啪啪视频` : '分类未找到 - 啪啪视频',
    description: category
      ? `浏览啪啪视频上的${category.name}高清视频，在线播放多种类型影片，发现更多精彩内容。`
      : '该分类不存在，返回啪啪视频首页发现更多高清视频内容。',
    keywords: category ? `啪啪视频,${category.name},高清视频,在线播放,视频资源` : '啪啪视频,分类',
  });

  if (!category) {
    return (
      <Layout>
        <div className="pt-16">
          <div className="px-4 md:px-6 py-24 text-center">
            <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-500">
              <i className="ri-error-warning-line text-3xl" />
            </span>
            <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">分类未找到</h1>
            <p className="mt-2 text-sm text-foreground-600">你访问的分类不存在或已下线。</p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-md bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors"
            >
              <i className="ri-home-4-line text-base" />
              返回首页
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const chip = (label: string, active: boolean, onClick: () => void) => (
    <button
      key={label}
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
        active
          ? 'bg-accent-500 text-background-50 font-semibold'
          : 'bg-background-100 text-foreground-700 border border-background-200/70 hover:border-accent-500 hover:text-accent-500'
      }`}
    >
      {label}
    </button>
  );

  const filterRow = (title: string, options: string[], value: string, onChange: (v: string) => void) => (
    <div className="flex items-start gap-3">
      <span className="shrink-0 pt-1.5 text-xs md:text-sm text-foreground-500 w-10">{title}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => chip(opt, value === opt, () => onChange(opt)))}
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="pt-16">
        <div className="px-4 md:px-6 py-6 md:py-8 border-b border-background-200/70 bg-background-100/50">
          <nav className="flex items-center gap-1.5 text-xs md:text-sm text-foreground-500 mb-4" aria-label="面包屑">
            <Link to="/" className="hover:text-accent-500 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-base" />
            <span className="text-foreground-900">{category.name}</span>
          </nav>
          <div className="flex items-center gap-3">
            <span className="w-12 h-12 flex items-center justify-center rounded-md bg-accent-500/15 text-accent-500">
              <i className={`${category.icon} text-2xl`} />
            </span>
            <div>
              <h1 className="font-heading text-xl md:text-2xl font-extrabold text-foreground-950">
                {category.name}
              </h1>
              <p className="mt-1 text-sm text-foreground-600 max-w-2xl">{category.description}</p>
            </div>
          </div>
        </div>

        <div className="px-4 md:px-6 py-6">
          <div className="rounded-lg border border-background-200/70 bg-background-100 p-4 md:p-5 space-y-4">
            <div className="pv-scroll overflow-x-auto">
              <div className="space-y-4 min-w-max">
                {filterRow('类型', tags, tag, setTag)}
                {filterRow('地区', regions, region, setRegion)}
                {filterRow('年代', years, year, setYear)}
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap border-t border-background-200/60 pt-4">
              <span className="text-xs md:text-sm text-foreground-500">排序</span>
              <div className="inline-flex p-1 rounded-full bg-background-50 border border-background-200/70">
                {SORTS.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setSort(s.key)}
                    className={`px-3.5 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                      sort === s.key
                        ? 'bg-accent-500 text-background-50 font-semibold'
                        : 'text-foreground-700 hover:text-foreground-950'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
              <span className="ml-auto text-xs md:text-sm text-foreground-500">
                共 {filtered.length} 部内容
              </span>
            </div>
          </div>
        </div>

        <div className="px-4 md:px-6 pb-12 md:pb-16">
          {pageItems.length > 0 ? (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {pageItems.map((video) => (
                  <VideoCard key={video.id} video={video} showCategory={false} />
                ))}
              </div>
              <Pagination current={page} total={totalPages} onChange={(p) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
            </>
          ) : (
            <div className="py-20 text-center">
              <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-500">
                <i className="ri-inbox-2-line text-3xl" />
              </span>
              <h2 className="mt-5 font-heading text-lg font-bold text-foreground-950">没有找到符合条件的视频</h2>
              <p className="mt-2 text-sm text-foreground-600">试试更换筛选条件，或返回首页看看其他精彩内容。</p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() => { setTag('全部'); setRegion('全部'); setYear('全部'); }}
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors"
                >
                  <i className="ri-refresh-line text-base" />
                  重置筛选
                </button>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-md border border-background-200/70 text-foreground-800 text-sm font-medium hover:border-accent-500 hover:text-accent-500 transition-colors"
                >
                  <i className="ri-home-4-line text-base" />
                  返回首页
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}